import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GridColDef, GridColumnResizeParams, GridColumnVisibilityModel } from "@mui/x-data-grid-pro";
import { TableColumnStorageConfig } from "../types";
import {
    clearTableColumnConfig,
    loadTableColumnConfig,
    saveTableColumnConfig,
    TableColumnStorageKey,
} from "../utils/saveTableColumnConfig";
import {
    ColumnLayout,
    columnLayoutsEqual,
    normalizeVisibility,
    orderColumns,
    pinFieldsLast,
    reconcileColumnOrder,
    resolveColumnLayout,
    visibilityToPersist,
} from "../utils/tableColumnLayout";

/** What the columns panel needs to reorder and reset the columns (see `Table`). */
export interface ColumnManagement {
    order: string[];
    /** Never listed in the panel and never moved, e.g. the row actions. */
    fixedFields: string[];
    /** Takes the full new column order, hidden columns included. */
    onOrderChange: (order: string[]) => void;
    onReset: () => void;
    canReset: boolean;
}

interface Options<C extends GridColDef> {
    storageKey: TableColumnStorageKey;
    /** The default columns in their default order. */
    columns: C[];
    /** Missing fields are visible. */
    defaultVisibility?: GridColumnVisibilityModel;
    /** Shown regardless of the saved config (e.g. filter-driven) and never persisted as visible. */
    forceVisible?: string[];
    fixedFields?: string[];
    persistWidths?: boolean;
    /** Off: the columns come back in default order, nothing is read or persisted. */
    enabled?: boolean;
    /** Adapts a saved config before it is applied. Must be stable and return its input when nothing changes. */
    migrate?: (saved: TableColumnStorageConfig) => TableColumnStorageConfig;
    /** Extra keys written with every save, e.g. the brand stamp. */
    stamp?: Partial<TableColumnStorageConfig>;
}

const NO_FIELDS: string[] = [];

/**
 * Order, visibility and (optionally) widths of a table's columns, persisted in
 * localStorage. Header drag and the columns panel both go through the returned
 * `gridProps`, so the table, the panel and the CSV export share one order.
 */
export const useTableColumnConfig = <C extends GridColDef>({
    storageKey,
    columns,
    defaultVisibility,
    forceVisible = NO_FIELDS,
    fixedFields = NO_FIELDS,
    persistWidths = false,
    enabled = true,
    migrate,
    stamp,
}: Options<C>) => {
    const defaultFields = useMemo(() => columns.map((col) => col.field), [columns]);
    const fieldsKey = defaultFields.join("|");
    const forceKey = forceVisible.join("|");
    const fixedKey = fixedFields.join("|");
    const defaultVisibilityKey = JSON.stringify(defaultVisibility ?? {});
    const stampKey = JSON.stringify(stamp ?? {});

    // Handlers and the resolve step read the latest options through this ref, so they
    // stay stable and can't act on a stale render.
    const optionsRef = useRef({ storageKey, defaultFields, defaultVisibility, forceVisible, fixedFields, persistWidths, migrate, stamp });
    optionsRef.current = { storageKey, defaultFields, defaultVisibility, forceVisible, fixedFields, persistWidths, migrate, stamp };

    const resolveFromStorage = (withSaved: boolean) => {
        const options = optionsRef.current;
        const saved = withSaved ? loadTableColumnConfig(options.storageKey) : null;
        const migrated = saved && options.migrate ? options.migrate(saved) : saved;
        const resolved = resolveColumnLayout({
            saved: migrated,
            defaultFields: options.defaultFields,
            defaultVisibility: options.defaultVisibility,
            forceVisible: options.forceVisible,
            fixedFields: options.fixedFields,
        });
        const layout = options.persistWidths ? resolved.layout : { ...resolved.layout, widths: {} };
        return {
            layout,
            persistedVisibility: resolved.persistedVisibility,
            needsWrite: !!saved && (resolved.orderChanged || migrated !== saved),
        };
    };

    const [initial] = useState(() => resolveFromStorage(enabled));
    const [layout, setLayout] = useState<ColumnLayout>(initial.layout);
    const layoutRef = useRef(initial.layout);
    const persistedVisibilityRef = useRef(initial.persistedVisibility);

    const save = useCallback((next: ColumnLayout, persistedVisibility: Record<string, boolean>) => {
        const { storageKey: key, stamp: extra } = optionsRef.current;
        saveTableColumnConfig(key, {
            columnOrder: next.order,
            columnVisibilityModel: persistedVisibility,
            columnWidths: next.widths,
            ...extra,
        });
    }, []);

    const commit = useCallback((next: ColumnLayout, persistedVisibility: Record<string, boolean>) => {
        layoutRef.current = next;
        persistedVisibilityRef.current = persistedVisibility;
        setLayout(next);
        save(next, persistedVisibility);
    }, [save]);

    // Re-resolve when the column set, the forced columns or the migration change (brand
    // switch, filter-driven columns, a column added for the user's permissions). Keyed on
    // the field list rather than the column identity, so a language switch doesn't re-run it.
    useEffect(() => {
        if (!enabled) return;
        const resolved = resolveFromStorage(true);
        layoutRef.current = resolved.layout;
        persistedVisibilityRef.current = resolved.persistedVisibility;
        setLayout((prev) => (columnLayoutsEqual(prev, resolved.layout) ? prev : resolved.layout));
        if (resolved.needsWrite) save(resolved.layout, resolved.persistedVisibility);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, storageKey, fieldsKey, forceKey, fixedKey, defaultVisibilityKey, stampKey, migrate]);

    const handleVisibilityChange = useCallback((model: GridColumnVisibilityModel) => {
        const { defaultFields: fields, forceVisible: forced } = optionsRef.current;
        // "Show all" in MUI's panel deletes keys instead of setting them to true, so
        // store an explicit value for every column.
        const visibility = normalizeVisibility(model, fields);
        commit(
            { ...layoutRef.current, visibility },
            visibilityToPersist(visibility, persistedVisibilityRef.current, forced)
        );
    }, [commit]);

    const handleOrderChange = useCallback((order: string[]) => {
        const { defaultFields: fields, fixedFields: fixed } = optionsRef.current;
        const next = pinFieldsLast(reconcileColumnOrder(order, fields), fixed);
        const current = layoutRef.current.order;
        if (next.length === current.length && next.every((field, i) => field === current[i])) return;
        commit({ ...layoutRef.current, order: next }, persistedVisibilityRef.current);
    }, [commit]);

    const handleWidthChange = useCallback((params: GridColumnResizeParams) => {
        const widths = { ...layoutRef.current.widths, [params.colDef.field]: params.width };
        commit({ ...layoutRef.current, widths }, persistedVisibilityRef.current);
    }, [commit]);

    // Reset drops the saved config entirely, so later changes to the defaults reach
    // these users too.
    const handleReset = useCallback(() => {
        clearTableColumnConfig(optionsRef.current.storageKey);
        const resolved = resolveFromStorage(false);
        layoutRef.current = resolved.layout;
        persistedVisibilityRef.current = resolved.persistedVisibility;
        setLayout(resolved.layout);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const defaultLayout = useMemo(
        () => resolveFromStorage(false).layout,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [fieldsKey, forceKey, fixedKey, defaultVisibilityKey]
    );
    const canReset = !columnLayoutsEqual(layout, defaultLayout);

    const orderedColumns = useMemo(
        () => (enabled ? orderColumns(columns, layout.order) : columns),
        [enabled, columns, layout.order]
    );

    const columnManagement = useMemo<ColumnManagement>(() => ({
        order: layout.order,
        fixedFields,
        onOrderChange: handleOrderChange,
        onReset: handleReset,
        canReset,
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [layout.order, canReset, handleOrderChange, handleReset, fixedKey]);

    if (!enabled) {
        return {
            columns: orderedColumns,
            columnVisibilityModel: undefined,
            columnWidths: {} as Record<string, number>,
            gridProps: { columns: orderedColumns },
        };
    }

    return {
        columns: orderedColumns,
        columnVisibilityModel: layout.visibility,
        columnWidths: layout.widths,
        gridProps: {
            columns: orderedColumns,
            columnVisibilityModel: layout.visibility,
            onColumnVisibilityModelChange: handleVisibilityChange,
            ...(persistWidths && { onColumnWidthChange: handleWidthChange }),
            columnManagement,
        },
    };
};
